/**
 * TripSettlementSummary — Live-Berechnungsvorschau, „Abrechnen"-Aktion und
 * druckfreundliche abgerechnete Ansicht einer Dienstreise (RK-7) inkl. Auslands-
 * Zweig (RK-15, architecture-reisekosten.md §13.8).
 *
 * Zwei Darstellungs-Modi:
 *   - `status === 'draft'`: rechnet den AKTUELLEN (ggf. noch nicht
 *     gespeicherten) Formularzustand live über {@link calculateTravelAllowances}
 *     vor (§3.2) und zeigt das Ergebnis als „Berechnungsvorschlag" — NIE als
 *     „steuerlich korrekt/geprüft/rechtssicher" (Wording-Gate V15, §9). Der
 *     3-Monats-Frist-Hinweis ist TRAGEND (V14) und wird als eigener,
 *     permanent sichtbarer Block gerendert (NICHT als Tooltip).
 *   - `status !== 'draft'`: zeigt den PERSISTIERTEN `settlement.calc`-Snapshot
 *     (GoBD-Nachweis, unveränderlich) in einer druckfreundlichen Ansicht. Bei
 *     `status === 'settled'` bietet die Komponente zusätzlich die
 *     Storno-Aktion inkl. Pflicht-Begründung UND dem MVP-Limitations-Hinweis
 *     (O-5: eine bereits exportierte Pauschale braucht eine manuelle
 *     Generalumkehr — dieses MVP kennt den Export-Status selbst nicht und
 *     zeigt den Hinweis daher bei JEDEM Storno einer abgerechneten Reise).
 *
 * Auslands-Zweig (RK-15, `foreignRates` — optional, V19 Gate-Parity): wird NUR
 * über den Entscheidungs-Helfer `getForeignRatesForUser` (config/travelExpensesAccess.ts)
 * befüllt verdrahtet, NIE über eine eigene Gate-Logik in dieser Komponente. Ist
 * kein Auslands-Tag im Ergebnis enthalten (reine Inlandsreise, auch mit offenem
 * Gate — Soll-Paar #39), bleibt die Darstellung BYTE-IDENTISCH zum MVP-Bestand
 * (kein zusätzlicher Spalten-/Hinweis-Block).
 *
 * @module components/travel/TripSettlementSummary
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconGlobe, IconInvoice, IconPrint, IconWarning } from '../../icons';
import {
  calculateTravelAllowances,
  TravelCalcInput,
  TravelCalcResult,
  TravelCalcWarningCode,
} from '../../utils/travel/travelAllowance';
import { TRAVEL_RATES } from '../../utils/travel/travelRates';
import type { TravelForeignRatesConfig } from '../../utils/travel/travelRates';
import { TRAVEL_RATES_FOREIGN } from '../../utils/travel/travelRatesForeign.generated';
import { resolveAppliedForeignVintages } from '../../utils/travel/travelRatesResolver';
import { TravelSettlement, TravelSettlementLinkedExpense, TravelTripStatus } from '../../types/travelTypes';
import { centsToEuros } from '../../utils/moneyUtils';
import { formatDate } from '../../utils/formatters';

export interface TripSettlementSummaryProps {
  status: TravelTripStatus;
  /** Live-Rechenkern-Eingabe aus dem AKTUELLEN Formularzustand — nur für `status === 'draft'` relevant. */
  calcInput: TravelCalcInput | null;
  /** Persistierter Snapshot (`status !== 'draft'`). */
  settlement: TravelSettlement | null;
  /** Nur bei `status === 'cancelled'` gesetzt. */
  cancellationReason?: string | null;
  cancelledAtISO?: string | null;
  onSettle?: () => void | Promise<void>;
  /**
   * Bug-3-Fix (bug-analyse-2026-07-17.md): wenn gesetzt und `onSettle` (noch)
   * fehlt, wird der Abrechnen-Bereich TROTZDEM gerendert — disabled, mit
   * erklärendem Hinweistext — statt komplett zu verschwinden. Bisher galt: vor
   * dem ersten Speichern (keine `selectedTripId`) gibt es kein `onSettle`
   * ⇒ der gesamte Bereich fehlte, wirkte für den Nutzer wie eine Sackgasse.
   * Aktuell nur `'notSaved'`. Im reinen Read-Only-Kontext bleibt der Aufrufer
   * bei `undefined`/`null` — der Bereich verschwindet dort weiterhin ganz
   * (unverändertes Verhalten, kein Speichern ohnehin möglich).
   */
  settleBlockedReason?: 'notSaved' | null;
  settling?: boolean;
  settleError?: string | null;
  onCancel?: (reason: string) => void | Promise<void>;
  cancelling?: boolean;
  cancelError?: string | null;
  /**
   * NEU (RK-15, §13.7/§13.8): Auslands-Sätze für die Live-Vorschau — AUSSCHLIESSLICH über
   * `getForeignRatesForUser(uid, tier)` beziehen (V19 Gate-Parity mit `settleTravelTrip`).
   * `undefined` (Gate zu, oder gar nicht übergeben) ⇒ byte-identisches MVP-Inland-Verhalten.
   */
  foreignRates?: TravelForeignRatesConfig;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE');
}

/**
 * Anzeigefertige Herkunfts-Angaben des Auslands-Jahrgangs (R3-3, Jurist Re-Loop 4).
 * Die Werte stammen AUSSCHLIESSLICH aus `TravelForeignRatesConfig.vintages` —
 * kein hartkodiertes Aktenzeichen, kein hartkodiertes Datum. Dieselbe Bindung an
 * die Daten-SoT, die der Jurist für `durationMonths` genehmigt hat, und aus
 * demselben Grund: eine handgepflegte Fundstelle driftet vom Datensatz ab.
 */
interface ForeignRatesSource {
  sourceAz: string;
  sourceBstbl: string;
  /** Bereits lokalisiert formatiertes `validFromISO`. */
  validFrom: string;
}

/**
 * Ermittelt die Fundstellen der TATSÄCHLICH ANGEWANDTEN Auslands-Jahrgänge — eine Zeile je
 * Jahrgang, chronologisch (R4-1).
 *
 * Vorher wählte diese Stelle datumsunabhängig „den offenen Jahrgang, sonst den letzten". Solange
 * es nur einen Jahrgang gab, war das unauffällig; ab dem zweiten wären Anzeige und Berechnung
 * auseinandergelaufen — unter Beträgen, die in eine Steuererklärung wandern, ist die Fundstelle
 * genau die Angabe, die stimmen muss (GoBD-Nachweis der Bemessungsgrundlage). Jetzt läuft die
 * Auswahl über denselben datumsgetriebenen Resolver wie die Berechnung
 * ({@link resolveAppliedForeignVintages}), gespeist aus den Reisetagen, die real über einen
 * Auslands-Satz aufgelöst wurden (`rateSource` gesetzt).
 *
 * Eine Reise über den Jahreswechsel weist dadurch ZWEI Quellzeilen aus.
 *
 * 🔴 A-8: Der interne Freigabestand des Datenbestands (Feld in `travelRates.ts`, dort dokumentiert)
 * ist streng intern und darf hier NIE auftauchen — weder als Datum noch als
 * „bis TT.MM.JJJJ geprüft"-Aussage; das wäre im Umkehrschluss eine Beschaffenheitsangabe für rund
 * 230 Ländersätze (§ 443 BGB). Zulässig sind ausschließlich `sourceAz`, `sourceBstbl` und das
 * lokalisierte `validFromISO` des tatsächlich angewandten Jahrgangs.
 *
 * @returns Leeres Array, wenn kein Auslandstag gerechnet wurde oder kein Jahrgang zum Reisedatum
 *          passt — dann entfällt der Quellen-Block ersatzlos (fail-safe: lieber keine Fundstelle
 *          als eine erfundene).
 */
function buildForeignRatesSources(
  calc: Extract<TravelCalcResult, { ok: true }>,
  config: TravelForeignRatesConfig,
  locale: string,
): ForeignRatesSource[] {
  const foreignDayDates = calc.days.filter((day) => day.rateSource !== undefined).map((day) => day.dateISO);
  if (foreignDayDates.length === 0) {
    return [];
  }
  return resolveAppliedForeignVintages(foreignDayDates, config)
    .filter((vintage) => Boolean(vintage.sourceAz) && Boolean(vintage.sourceBstbl) && Boolean(vintage.validFromISO))
    .map((vintage) => ({
      sourceAz: vintage.sourceAz,
      sourceBstbl: vintage.sourceBstbl,
      validFrom: formatDate(new Date(vintage.validFromISO), { locale }),
    }));
}

/** Belegdatum ('YYYY-MM-DD' oder `null`) für die Ist-Kosten-Tabelle — `null`/leer ⇒ Gedankenstrich. */
function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('de-DE');
}

/** Warnungen außer der tragenden 3-Monats-Frist (die bekommt einen eigenen, prominenteren Block). */
function isSecondaryWarning(code: TravelCalcWarningCode): boolean {
  return code !== 'threeMonthRuleNotChecked';
}

/**
 * Lokalisierter Ländername via `Intl.DisplayNames` (§13.8 — kein eigenes Übersetzungs-Asset).
 * Fällt defensiv auf den ISO-Code zurück, falls `Intl.DisplayNames` fehlt/den Code nicht kennt.
 */
function countryLabel(code: string, lang: string): string {
  try {
    return new Intl.DisplayNames([lang || 'de'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Tagestabelle des Berechnungsergebnisses — für Live-Vorschau UND persistierten Snapshot identisch. */
const CalcDaysTable: React.FC<{ calc: Extract<TravelCalcResult, { ok: true }> }> = ({ calc }) => {
  const { t, i18n } = useTranslation('travel');
  // Herkunfts-Spalte NUR rendern, wenn mind. ein Tag über einen Auslands-Satz aufgelöst wurde
  // (Inland-Null-Delta — reine DE-Reisen zeigen exakt die MVP-Bestandstabelle, Soll-Paar #39).
  const hasForeignDay = calc.days.some((day) => day.rateSource !== undefined);

  return (
    <div className="overflow-x-auto rounded-lg border border-hairline">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[rgb(var(--surface-base-rgb)/0.6)] text-muted uppercase tracking-wide">
            <th className="px-3 py-2 text-left font-medium">{t('settlement.columns.date')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('settlement.columns.dayType')}</th>
            {hasForeignDay && (
              <th className="px-3 py-2 text-left font-medium">{t('settlement.columns.origin', 'Herkunft')}</th>
            )}
            <th className="px-3 py-2 text-right font-medium">{t('settlement.columns.base')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('settlement.columns.reduction')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('settlement.columns.allowance')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[color:var(--border-hairline)]">
          {calc.days.map((day) => {
            const reductionCents = day.reductionBreakfastCents + day.reductionLunchCents + day.reductionDinnerCents;
            return (
              <tr key={day.dateISO}>
                <td className="px-3 py-2 text-secondary whitespace-nowrap">{day.dateISO}</td>
                <td className="px-3 py-2 text-muted">{t(`settlement.dayType.${day.dayType}`)}</td>
                {hasForeignDay && (
                  <td className="px-3 py-2 text-muted">
                    <div className="flex flex-col gap-0.5">
                      <span>{countryLabel(day.countryCode, i18n.language)}</span>
                      {day.rateSource && (
                        <span className="text-[10px] uppercase tracking-wide text-accent-ink">
                          {t(`settlement.rateSource.${day.rateSource}`, day.rateSource)}
                        </span>
                      )}
                    </div>
                  </td>
                )}
                <td className="px-3 py-2 text-right font-mono text-secondary">{formatCents(day.baseCents)}</td>
                <td className="px-3 py-2 text-right font-mono text-faint">
                  {reductionCents > 0 ? `−${formatCents(reductionCents)}` : '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono text-primary font-medium">
                  {formatCents(day.allowanceCents)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Summenzeile + tragender 3-Monats-Hinweis + Ausland-Quellenblock (falls relevant) + restliche Warnungen. */
const CalcTotals: React.FC<{
  calc: Extract<TravelCalcResult, { ok: true }>;
  /**
   * Herkunfts-Metadaten JE tatsächlich angewandtem Auslands-Jahrgang (R3-3/R4-1), chronologisch.
   * Leeres Array ⇒ kein Quellen-Block (fail-safe: lieber keine Fundstelle als eine erfundene);
   * eine Reise über den Jahreswechsel liefert zwei Einträge.
   */
  foreignSources: ForeignRatesSource[];
}> = ({ calc, foreignSources }) => {
  const { t } = useTranslation('travel');
  const secondaryWarnings = calc.warnings.filter(isSecondaryWarning);
  const hasForeignDay = calc.days.some((day) => day.rateSource !== undefined);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-[rgb(var(--surface-base-rgb)/0.6)] rounded-lg px-3 py-2">
          <p className="text-xs text-faint">{t('settlement.totals.verpflegung')}</p>
          <p className="text-primary font-mono font-semibold">{formatCents(calc.verpflegungCents)}</p>
        </div>
        <div className="bg-[rgb(var(--surface-base-rgb)/0.6)] rounded-lg px-3 py-2">
          <p className="text-xs text-faint">{t('settlement.totals.mileage')}</p>
          <p className="text-primary font-mono font-semibold">{formatCents(calc.mileageCents)}</p>
        </div>
        {/* Sonderfall §2.3.1: Akzent-Highlight-Kachel (Marken-Betonung der Gesamtsumme, kein
            Status/Hinweis) — die frühere dunkle Indigo-Tönung (900/30) weicht dem
            Mint-Alpha-Tint (500/10, Mint-Hex trägt den Alpha-Modifier gate-frei,
            funktioniert in beiden Themes). */}
        <div className="bg-indigo-500/10 rounded-lg px-3 py-2">
          <p className="text-xs text-accent-ink">{t('settlement.totals.total')}</p>
          <p className="text-primary font-mono font-bold">{formatCents(calc.totalCents)}</p>
        </div>
      </div>

      {/* V14 — tragender Dauerhinweis, PROMINENT, eigener Block (kein Tooltip). */}
      <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg px-3 py-2.5">
        <IconWarning size={14} className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]" />
        <p className="text-xs text-[var(--tone-warning-fg)]">{t('settlement.threeMonthNotice')}</p>
      </div>

      {/* RK-15 (§13.8): Quellen-/Stand-Block + Ort-vor-Mitternacht-/Seetage-Hinweis (O-15) —
          NUR wenn mind. ein Tag tatsächlich über einen Auslands-Satz gerechnet wurde
          (Inland-Null-Delta, Soll-Paar #39: kein Block trotz offenem Gate ohne Ausland-Tag).

          R3-3 (Jurist, Re-Loop 4): hier stand bis zum GA-Flip „Ausland: Beta —
          … noch nicht abschließend steuerlich abgenommen". PR #316 hat den
          Auslands-Zweig scharf geschaltet (O-11/O-13 von e-rechnung abgenommen,
          `.env.production:192-194`), den Warnhinweis aber stehen gelassen. Die
          Aussage war damit unwahr — und zwar ausgerechnet dort, wo ein zahlender
          Ultra-Kunde die Beträge für seine Buchführung abliest (§ 5a Abs. 2 Nr. 2
          UWG, Unklarheit zulasten des Anbieters).

          Ersetzt durch den Quellen-/Stand-Block aus R3-2: für Zahlen, die in die
          Steuererklärung wandern, ist die Herkunft die entscheidende Angabe
          (Nachprüfbarkeit gegenüber Steuerberater/Betriebsprüfung). Die
          Metadaten waren bereits vollständig vorhanden (`sourceAz`,
          `sourceBstbl`, `validFromISO` je Vintage) und wurden nur nirgends
          ausgegeben — derselbe GoBD-Nachvollziehbarkeits-Zweck, den
          `travelRates.ts:9-11` schon selbst formuliert. */}
      {hasForeignDay && (
        <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5">
          <IconGlobe size={14} className="mt-0.5 shrink-0 text-[var(--tone-info-fg)]" />
          <div className="space-y-1">
            {foreignSources.length > 0 && (
              <>
                <p className="text-xs font-medium text-[var(--tone-info-fg)]">
                  {t('settlement.foreignRatesSourceTitle', 'Auslandssätze: Quelle')}
                </p>
                {/* R4-1: eine Zeile je tatsächlich angewandtem Jahrgang — eine Reise über den
                    Jahreswechsel weist beide BMF-Schreiben aus, statt pauschal eines zu nennen. */}
                {foreignSources.map((source) => (
                  <p key={source.sourceAz + source.validFrom} className="text-xs text-[var(--tone-info-fg)]">
                    {t('settlement.foreignRatesSourceHint', {
                      sourceAz: source.sourceAz,
                      sourceBstbl: source.sourceBstbl,
                      validFrom: source.validFrom,
                      defaultValue:
                        'Die Auslands-Verpflegungspauschalen folgen dem BMF-Schreiben {{sourceAz}} ({{sourceBstbl}}), gültig ab {{validFrom}}. Für nicht gesondert gelistete Länder gilt der Satz für Luxemburg (BMF-Auffangregel). Maßgeblich für Ihre Steuererklärung bleibt das jeweils aktuelle BMF-Schreiben.',
                    })}
                  </p>
                ))}
              </>
            )}
            <p className="text-xs text-[var(--tone-info-fg)]">
              {t(
                'settlement.foreignLocationHint',
                'Maßgeblich ist der Ort, den Sie vor 24 Uhr Ortszeit erreichen (An-/Zwischentag) bzw. Ihr letzter Tätigkeitsort im Ausland (Abreisetag). Wählen Sie bei Seetagen oder Zwischenlandungen den Ort nach bestem Wissen – eine automatische Prüfung erfolgt nicht.',
              )}
            </p>
          </div>
        </div>
      )}

      {secondaryWarnings.length > 0 && (
        <ul className="space-y-1">
          {secondaryWarnings.map((code) => (
            <li key={code} className="text-xs text-muted flex items-start gap-1.5">
              <span className="text-faint">•</span>
              {t(`settlement.warnings.${code}`)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * RK-22 (§14.4, E18/E19): nachrichtlicher Ist-Kosten-Referenzblock + Gesamtkosten-Fußzeile.
 *
 * Rendert AUSSCHLIESSLICH den persistierten `settlement.linkedExpenses`-Snapshot (Settle-Zeit-
 * Momentaufnahme) — KEINE zusätzlichen Firestore-Reads (auch nicht in der Advisor-Ansicht),
 * KEIN DATEV-Effekt (V24/E18). Die verknüpften Belege sind bereits über den Ausgaben-Weg
 * gebucht; hier werden sie nur nachrichtlich der Reise zugeordnet. Zwei getrennte Teilsummen
 * (neu gebuchte Pauschalen/Kilometergeld vs. nachrichtliche Ist-Kosten) plus die Fußzeile
 * „Gesamtkosten der Reise". Druckfreundlich (bestehende `print:`-Klassen-Konvention).
 *
 * Wird vom Aufrufer NUR gerendert, wenn ein nicht-leerer Snapshot vorliegt (§14.4).
 */
const LinkedExpensesBlock: React.FC<{
  linkedExpenses: TravelSettlementLinkedExpense[];
  /** Neu gebuchte Pauschalen + Kilometergeld (`settlement.calc.totalCents`). */
  pauschalenCents: number;
}> = ({ linkedExpenses, pauschalenCents }) => {
  const { t } = useTranslation('travel');
  const istKostenCents = linkedExpenses.reduce((sum, e) => sum + e.grossCents, 0);
  const grandTotalCents = pauschalenCents + istKostenCents;

  // Druck = Papier (theme-agnostisch); Hex-Arbitrary statt Roh-Klasse hält das W2-Gate ohne
  // Allowlist-Eintrag grün (§2.3.3). Bildschirm-Klassen derselben Literale werden normal
  // gemappt; nur `print:text-black`/`print:hidden` bleiben unverändert (matchen das Pattern nicht).
  return (
    <div className="space-y-3 pt-3 border-t border-hairline print:border-[#d1d5db]">
      <h3 className="text-sm font-semibold text-primary print:text-black">{t('settlement.linkedExpensesTitle')}</h3>

      <div className="overflow-x-auto rounded-lg border border-hairline print:border-[#d1d5db]">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[rgb(var(--surface-base-rgb)/0.6)] text-muted uppercase tracking-wide print:bg-[#f3f4f6] print:text-[#374151]">
              <th className="px-3 py-2 text-left font-medium">{t('settlement.linkedExpensesColumns.supplier')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('settlement.linkedExpensesColumns.date')}</th>
              <th className="px-3 py-2 text-left font-medium">
                {t('settlement.linkedExpensesColumns.invoiceNumber')}
              </th>
              <th className="px-3 py-2 text-right font-medium">{t('settlement.linkedExpensesColumns.amount')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-hairline)] print:divide-[#d1d5db]">
            {linkedExpenses.map((expense) => (
              <tr key={expense.expenseId}>
                <td className="px-3 py-2 text-secondary print:text-black">{expense.supplierName || '—'}</td>
                <td className="px-3 py-2 text-muted whitespace-nowrap print:text-black">
                  {formatDateOnly(expense.expenseDateISO)}
                </td>
                <td className="px-3 py-2 text-muted print:text-black">{expense.invoiceNumber ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono text-secondary print:text-black">
                  {formatCents(expense.grossCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pflicht-Abgrenzungstext (§14.4): bereits gebucht — nur nachrichtlich, keine erneute Buchung. */}
      <p className="text-xs text-faint print:text-[#374151]">{t('settlement.linkedExpensesNote')}</p>
      {/* Snapshot-Abgrenzungstext (A2, rev.5): Stand zum Abrechnungszeitpunkt; maßgeblich bleibt der Einzelbeleg. */}
      <p className="text-xs text-faint print:text-[#374151]">{t('settlement.linkedExpensesSnapshotNote')}</p>

      {/* Getrennte Teilsummen + Fußzeile „Gesamtkosten der Reise". */}
      <div className="space-y-1.5 pt-1 text-sm">
        <div className="flex items-center justify-between text-muted print:text-black">
          <span>{t('settlement.totals.total')}</span>
          <span className="font-mono">{formatCents(pauschalenCents)}</span>
        </div>
        <div className="flex items-center justify-between text-muted print:text-black">
          <span>{t('settlement.totals.linkedExpenses')}</span>
          <span className="font-mono">{formatCents(istKostenCents)}</span>
        </div>
        <div className="flex items-center justify-between pt-1.5 border-t border-hairline print:border-[#d1d5db] text-primary print:text-black font-semibold">
          <span>{t('settlement.totals.grandTotal')}</span>
          <span className="font-mono font-bold">{formatCents(grandTotalCents)}</span>
        </div>
      </div>
      <p className="text-xs text-faint print:text-[#374151]">{t('settlement.grandTotalNote')}</p>
    </div>
  );
};

/**
 * Berechnungsvorschau (draft) bzw. abgerechnete/stornierte Ansicht einer Reise.
 */
const TripSettlementSummary: React.FC<TripSettlementSummaryProps> = ({
  status,
  calcInput,
  settlement,
  cancellationReason,
  cancelledAtISO,
  onSettle,
  settleBlockedReason = null,
  settling = false,
  settleError = null,
  onCancel,
  cancelling = false,
  cancelError = null,
  foreignRates,
}) => {
  const { t, i18n } = useTranslation('travel');
  const [cancelReason, setCancelReason] = useState('');

  // R3-3/R4-1: Herkunfts-Metadaten je angewandtem Auslands-Jahrgang.
  // Speist sich primär aus derselben `foreignRates`-Konfiguration, mit der auch gerechnet wird
  // (V19 Gate-Parity) — die angezeigte Fundstelle kann daher nicht von den verwendeten Sätzen
  // abweichen. Fallback auf das statische Asset ist NUR für die Anzeige eines bereits
  // abgerechneten Snapshots relevant (Gate-Helfer liefert dort ggf. `undefined`): Vintages sind
  // append-only, Alt-Jahrgänge bleiben also auffindbar. Das ist reine Metadaten-Anzeige und kein
  // Berechnungs-Gate — gerechnet wird ausschließlich mit dem persistierten Snapshot.
  const sourceConfig = foreignRates ?? TRAVEL_RATES_FOREIGN;

  const liveResult = useMemo((): TravelCalcResult | null => {
    if (status !== 'draft' || calcInput === null) return null;
    // V19 Gate-Parity: `foreignRates` kommt AUSSCHLIESSLICH über `getForeignRatesForUser`
    // (Aufrufer, TravelExpensesPage) — `undefined` verhält sich byte-identisch zum Weglassen
    // des 3. Parameters (Regel 10, travelAllowance.ts prüft `options?.foreignRates`).
    return calculateTravelAllowances(calcInput, TRAVEL_RATES, { foreignRates });
  }, [status, calcInput, foreignRates]);

  // ─── Draft: Live-Vorschau + Abrechnen ────────────────────────────────────
  if (status === 'draft') {
    return (
      <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5 space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-primary flex items-center gap-2">
            <IconInvoice className="text-accent-ink" />
            {t('settlement.previewTitle')}
          </h2>
          {/* Wording-Gate V15: NIE "steuerlich korrekt/geprüft/rechtssicher". */}
          <p className="text-xs text-faint mt-1">{t('settlement.previewDisclaimer')}</p>
        </div>

        {liveResult === null && <p className="text-xs text-faint">{t('settlement.previewIncomplete')}</p>}

        {liveResult !== null && !liveResult.ok && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg px-3 py-2.5">
            <p className="text-xs text-[var(--tone-danger-fg)]">{t(`settlement.errors.${liveResult.error}`)}</p>
          </div>
        )}

        {liveResult !== null && liveResult.ok && (
          <>
            <CalcDaysTable calc={liveResult} />
            <CalcTotals calc={liveResult} foreignSources={buildForeignRatesSources(liveResult, sourceConfig, i18n.language)} />
          </>
        )}

        {settleError && <p className="text-xs text-[var(--tone-danger-fg)]">{settleError}</p>}

        {/* Bug-3-Fix: der Bereich wird auch OHNE `onSettle` gerendert, solange der Aufrufer
            per `settleBlockedReason` signalisiert, dass die Aktion nur temporär (noch nicht
            gespeichert) fehlt — statt komplett zu verschwinden (kein toter Zustand mehr). */}
        {(onSettle || settleBlockedReason === 'notSaved') && (
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={() => onSettle?.()}
              disabled={!onSettle || !liveResult?.ok || settling}
              className="w-full md:w-auto px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-surface-inset disabled:text-faint disabled:cursor-not-allowed text-[#ffffff] rounded-lg transition-colors"
            >
              {settling ? t('settlement.settling') : t('settlement.settleButton')}
            </button>
            {!onSettle && settleBlockedReason === 'notSaved' && (
              <p className="text-xs text-faint">{t('settlement.settleRequiresSaveHint')}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Settled / cancelled: persistierter Snapshot, druckfreundlich ────────
  const calc = settlement?.calc ?? null;

  // Druck = Papier (theme-agnostisch); Hex-Arbitrary statt Roh-Klasse hält das W2-Gate ohne
  // Allowlist-Eintrag grün (§2.3.3).
  return (
    <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5 space-y-4 print:bg-[#ffffff] print:text-black">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-primary flex items-center gap-2 print:text-black">
          <IconPrint className="text-accent-ink print:hidden" />
          {t('settlement.finalTitle')}
        </h2>
      </div>
      <p className="text-xs text-faint print:text-[#374151]">{t('settlement.finalDisclaimer')}</p>

      {calc && (
        <>
          <CalcDaysTable calc={calc} />
          <CalcTotals calc={calc} foreignSources={buildForeignRatesSources(calc, sourceConfig, i18n.language)} />
        </>
      )}

      {/* RK-22 (§14.4): nachrichtlicher Ist-Kosten-Block + Gesamtkosten — NUR wenn ein
          nicht-leerer Settle-Zeit-Snapshot vorliegt (E19). Advisor-/Druckansicht liest
          ausschließlich diesen Snapshot aus dem Trip-Doc (keine zusätzlichen Reads). */}
      {calc && settlement?.linkedExpenses && settlement.linkedExpenses.length > 0 && (
        <LinkedExpensesBlock linkedExpenses={settlement.linkedExpenses} pauschalenCents={calc.totalCents} />
      )}

      {settlement && (
        <dl className="grid grid-cols-2 gap-2 text-xs text-muted pt-2 border-t border-hairline">
          <div>
            <dt className="text-faint">{t('settlement.belegLabel')}</dt>
            <dd className="text-secondary font-mono">{settlement.belegLabel}</dd>
          </div>
          <div>
            <dt className="text-faint">{t('settlement.settledAt')}</dt>
            <dd className="text-secondary">{formatDateTime(settlement.settledAtISO)}</dd>
          </div>
        </dl>
      )}

      {status === 'settled' && onCancel && (
        <div className="pt-3 border-t border-hairline space-y-2 print:hidden">
          <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg px-3 py-2.5">
            <IconWarning size={14} className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]" />
            <p className="text-xs text-[var(--tone-warning-fg)]">{t('settlement.stornoLimitationHint')}</p>
          </div>
          <textarea
            className="w-full bg-surface border border-hairline rounded-lg px-3 py-2 text-sm text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows={2}
            placeholder={t('settlement.cancelReasonPlaceholder')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          {cancelError && <p className="text-xs text-[var(--tone-danger-fg)]">{cancelError}</p>}
          <button
            type="button"
            onClick={() => onCancel(cancelReason)}
            disabled={cancelReason.trim().length === 0 || cancelling}
            className="px-4 py-2 text-sm font-medium bg-red-700 hover:bg-red-600 disabled:bg-surface-inset disabled:text-faint disabled:cursor-not-allowed text-[#ffffff] rounded-lg transition-colors"
          >
            {cancelling ? t('settlement.cancelling') : t('settlement.cancelButton')}
          </button>
        </div>
      )}

      {status === 'cancelled' && (
        <dl className="pt-3 border-t border-hairline text-xs text-muted space-y-1">
          <div>
            <dt className="inline text-faint">{t('settlement.cancellationReason')}: </dt>
            <dd className="inline text-secondary">{cancellationReason ?? '—'}</dd>
          </div>
          <div>
            <dt className="inline text-faint">{t('settlement.cancelledAt')}: </dt>
            <dd className="inline text-secondary">{formatDateTime(cancelledAtISO)}</dd>
          </div>
        </dl>
      )}
    </div>
  );
};

export default TripSettlementSummary;
